import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState, useEffect } from "react";

import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Table,
  Modal,
  Badge,
  Row,
  Col,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";
import RichTextEditor from "../../components/WYSIWYG/RichTextEditor";
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  getDocs,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import Swal from "sweetalert2";
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSave, 
  FaEye, 
  FaFileSignature,
  FaTimes,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationTriangle,
  FaCog,
  FaToggleOn,
  FaToggleOff,
  FaClipboardList
} from "react-icons/fa";

const db = getFirestore(firebaseApp);

function Solicitud({ usuario }) {
  // Estados para el formulario y gestión
  const [declaraciones, setDeclaraciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [declaracionActual, setDeclaracionActual] = useState({
    titulo: "",
    texto: "",
    id: null
  });
  const [showVistaPrevia, setShowVistaPrevia] = useState(false);
  const [declaracionPreview, setDeclaracionPreview] = useState(null);

  // Cargar declaraciones juradas al iniciar
  useEffect(() => {
    cargarDeclaraciones();
  }, [usuario]);

  // Función para cargar las declaraciones desde Firestore
  const cargarDeclaraciones = async () => {
    try {
      setLoading(true);
      const rucEmpresa = usuario?.ruc || "20538856674";

      // Usar la ruta correcta para las declaraciones juradas
      const declaracionesRef = collection(db, `onboarding-formatos/${rucEmpresa}/Solicitud`);
      const querySnapshot = await getDocs(declaracionesRef);

      const listaDeclaraciones = [];
      querySnapshot.forEach((doc) => {
        listaDeclaraciones.push({
          id: doc.id,
          ...doc.data()
        });
      });

      setDeclaraciones(listaDeclaraciones);
    } catch (error) {
      console.error("Error al cargar declaraciones juradas:", error);
      setError("Error al cargar las declaraciones juradas. Inténtelo de nuevo más tarde.");
    } finally {
      setLoading(false);
    }
  };

  // Función para guardar la solicitud
  const guardarDeclaracion = async (e) => {
    e.preventDefault();
    
    if (!declaracionActual.titulo.trim()) {
      setError("Debe ingresar un título para la solicitud");
      return;
    }

    if (!declaracionActual.texto.trim()) {
      setError("El contenido de la solicitud no puede estar vacío");
      return;
    }

    try {
      setGuardando(true);
      setError(null);
      
      const rucEmpresa = usuario?.ruc || "20538856674";
      
      // Determinar si es un nuevo documento o una actualización
      const docId = modoEdicion ? declaracionActual.id : `sol_${Date.now()}`;
      const declaracionRef = doc(db, `onboarding-formatos/${rucEmpresa}/Solicitud`, docId);
      
      // Datos a guardar con la estructura correcta
      const declaracionData = {
        titulo: declaracionActual.titulo,
        texto: declaracionActual.texto,
        activo: true
      };
      
      // Guardar en Firestore
      await setDoc(declaracionRef, declaracionData);
      
      // Mostrar mensaje de éxito
      Swal.fire({
        title: modoEdicion ? "Solicitud actualizada" : "Solicitud creada",
        text: modoEdicion 
          ? "La solicitud ha sido actualizada correctamente" 
          : "La solicitud ha sido creada correctamente",
        icon: "success",
        timer: 2000,
        showConfirmButton: false
      });
      
      // Resetear formulario y recargar datos
      resetearFormulario();
      cargarDeclaraciones();
      
    } catch (error) {
      console.error("Error al guardar solicitud:", error);
      setError("Error al guardar la solicitud. Inténtelo de nuevo.");
    } finally {
      setGuardando(false);
    }
  };

  // Función para cargar una Solicitud para edición
  const editarDeclaracion = async (id) => {
    try {
      setLoading(true);
      const rucEmpresa = usuario?.ruc || "20538856674";
      
      const declaracionRef = doc(db, `onboarding-formatos/${rucEmpresa}/Solicitud`, id);
      const declaracionDoc = await getDoc(declaracionRef);
      
      if (declaracionDoc.exists()) {
        const data = declaracionDoc.data();
        setDeclaracionActual({
          id: id,
          titulo: data.titulo,
          texto: data.texto || ""
        });
        setModoEdicion(true);
        
        // Hacer scroll hacia el formulario
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        Swal.fire({
          title: "Error",
          text: "La solicitud no existe o ha sido eliminada",
          icon: "error"
        });
      }
    } catch (error) {
      console.error("Error al cargar Solicitud para editar:", error);
      setError("Error al cargar la Solicitud para editar");
    } finally {
      setLoading(false);
    }
  };

  // Función para ver una Solicitud
  const verDeclaracion = (declaracion) => {
    setDeclaracionPreview(declaracion);
    setShowVistaPrevia(true);
  };

  // Función para eliminar una Solicitud
  const eliminarDeclaracion = async (id) => {
    // Mostrar confirmación antes de eliminar
    const confirmResult = await Swal.fire({
      title: "¿Eliminar Solicitud?",
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545"
    });
    
    if (!confirmResult.isConfirmed) return;
    
    try {
      setLoading(true);
      const rucEmpresa = usuario?.ruc || "20538856674";
      
      const declaracionRef = doc(db, `onboarding-formatos/${rucEmpresa}/Solicitud`, id);
      await deleteDoc(declaracionRef);
      
      Swal.fire({
        title: "Eliminada",
        text: "La solicitud ha sido eliminada",
        icon: "success",
        timer: 1500,
        showConfirmButton: false
      });
      
      // Resetear formulario si estábamos editando esta Solicitud
      if (modoEdicion && declaracionActual.id === id) {
        resetearFormulario();
      }
      
      // Recargar lista
      cargarDeclaraciones();
      
    } catch (error) {
      console.error("Error al eliminar Solicitud:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudo eliminar la solicitud",
        icon: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // Función alternativa para desactivar en lugar de eliminar
  const toggleActivoDeclaracion = async (id, estadoActual) => {
    try {
      setLoading(true);
      const rucEmpresa = usuario?.ruc || "20538856674";
      
      const declaracionRef = doc(db, `onboarding-formatos/${rucEmpresa}/Solicitud`, id);
      await setDoc(declaracionRef, { activo: !estadoActual }, { merge: true });
      
      Swal.fire({
        title: estadoActual ? "Desactivada" : "Activada",
        text: `La solicitud ha sido ${estadoActual ? "desactivada" : "activada"}`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false
      });
      
      // Recargar lista
      cargarDeclaraciones();
      
    } catch (error) {
      console.error("Error al cambiar estado de la Solicitud:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudo cambiar el estado de la solicitud",
        icon: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // Resetear el formulario
  const resetearFormulario = () => {
    setModoEdicion(false);
    setDeclaracionActual({
      titulo: "",
      texto: "",
      id: null
    });
  };

  // Actualizar el contenido del editor
  const handleEditorChange = (contenido) => {
    setDeclaracionActual({
      ...declaracionActual,
      texto: contenido
    });
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderBottom: "3px solid #f39c12",
            }}
          >
            <div style={{position:"relative",display:"flex",alignItems:"center",justifyContent:"center"}} className="w-100">

              <div className="text-center">
                <h2 className="fw-bold mb-1">
                  <FaClipboardList className="me-3" />
                  Gestión de Solicitudes
                </h2>
                <p className="mb-0 opacity-75">
                  Administra las solicitudes personalizadas para el proceso de onboarding
                </p>
              </div>
              <div style={{position:"absolute",right:"20px"}}>
                <BotonManualHeader usuario={usuario} />
              </div>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Información del módulo */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "white"
                      }}
                    >
                      <FaCog size={24} />
                    </div>
                  </Col>
                  <Col md={11}>
                    <h5 className="mb-1 fw-semibold">Editor de Solicitudes</h5>
                    <p className="text-muted mb-0 small">
                      Crea y gestiona los formatos de solicitudes que serán utilizados por los empleados durante el proceso de onboarding
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Estados de error */}
            {error && (
              <Alert variant="danger" className="border-0 shadow-sm mb-4" style={{ borderRadius: "10px" }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <FaExclamationTriangle className="me-3 fs-4" />
                    <div>
                      <h6 className="fw-bold mb-1">Error</h6>
                      <p className="mb-0">{error}</p>
                    </div>
                  </div>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setError(null)}
                    className="text-danger p-0"
                  >
                    <FaTimes />
                  </Button>
                </div>
              </Alert>
            )}

            {/* Loading inicial */}
            {loading && !modoEdicion && (
              <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                <Card.Body className="py-5">
                  <div className="text-center">
                    <Spinner animation="border" variant="primary" style={{ width: "3rem", height: "3rem" }} />
                    <h5 className="mt-3 text-muted">Cargando solicitudes...</h5>
                    <p className="text-muted">Consultando las solicitudes disponibles</p>
                  </div>
                </Card.Body>
              </Card>
            )}

            {!loading && (
              <>
                {/* Formulario para crear/editar solicitud */}
                <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                  <Card.Header 
                    className="text-white py-3"
                    style={{
                      background: modoEdicion 
                        ? "linear-gradient(135deg, #fd7e14 0%, #ff8500 100%)" 
                        : "linear-gradient(135deg, #198754 0%, #157347 100%)",
                    }}
                  >
                    <div className="d-flex align-items-center">
                      {modoEdicion ? <FaEdit className="me-2" /> : <FaPlus className="me-2" />}
                      <div>
                        <h5 className="mb-0 fw-semibold">
                          {modoEdicion ? "Editar Solicitud" : "Nueva Solicitud"}
                        </h5>
                        <small className="opacity-75">
                          {modoEdicion 
                            ? "Modifica el contenido de la solicitud existente" 
                            : "Crea una nueva solicitud personalizada"}
                        </small>
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Form onSubmit={guardarDeclaracion}>
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold text-dark">Título de la Solicitud</Form.Label>
                        <Form.Control
                          type="text"
                          value={declaracionActual.titulo}
                          onChange={(e) => setDeclaracionActual({...declaracionActual, titulo: e.target.value})}
                          placeholder="Ej: Solicitud de permiso sin goce de haber"
                          required
                          className="form-control-lg"
                          style={{ borderRadius: "10px" }}
                        />
                        <Form.Text className="text-muted">
                          Ingrese un nombre descriptivo para identificar esta solicitud.
                        </Form.Text>
                      </Form.Group>
                      
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold text-dark">Contenido de la Solicitud</Form.Label>
                        <div 
                          style={{ 
                            borderRadius: "10px", 
                            overflow: "hidden",
                            border: "1px solid #dee2e6"
                          }}
                        >
                          <RichTextEditor
                            initialValue={declaracionActual.texto}
                            onSave={null}
                            onChange={handleEditorChange}
                            placeholder="Redacte aquí el contenido de la solicitud..."
                            height="400px"
                          />
                        </div>
                        <Form.Text className="text-muted">
                          Use el editor para dar formato al texto. Puede incluir párrafos, listas, negritas, etc.
                        </Form.Text>
                      </Form.Group>
                      
                      <div className="d-flex justify-content-between pt-3 border-top">
                        <Button 
                          variant="outline-secondary"
                          onClick={resetearFormulario}
                          disabled={guardando}
                          style={{ borderRadius: "10px" }}
                          className="px-4"
                        >
                          <FaTimes className="me-2" />
                          Cancelar
                        </Button>
                        
                        <Button 
                          variant="primary"
                          type="submit"
                          disabled={guardando}
                          style={{
                            borderRadius: "10px",
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            border: "none",
                          }}
                          className="px-4"
                        >
                          {guardando ? (
                            <>
                              <Spinner size="sm" animation="border" className="me-2" />
                              Guardando...
                            </>
                          ) : (
                            <>
                              <FaSave className="me-2" />
                              {modoEdicion ? "Actualizar Solicitud" : "Guardar Solicitud"}
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>

                {/* Lista de solicitudes */}
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Header className="bg-primary text-white py-3">
                    <div className="d-flex align-items-center">
                      <FaClipboardList className="me-2" />
                      <div className="flex-grow-1">
                        <h5 className="mb-0 fw-semibold">Solicitudes Disponibles</h5>
                        <small className="opacity-75">
                          Gestiona todas las solicitudes del sistema
                        </small>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <Badge bg="light" text="primary" style={{ fontSize: "0.9rem" }}>
                          {declaraciones.length} solicitudes
                        </Badge>
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {declaraciones.length === 0 ? (
                      <div className="text-center py-5">
                        <div
                          className="bg-light rounded-circle mx-auto mb-3"
                          style={{
                            width: "80px",
                            height: "80px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FaClipboardList size={32} className="text-muted" />
                        </div>
                        <h5 className="text-muted mb-2">No hay solicitudes</h5>
                        <p className="text-muted mb-3">
                          Cree una nueva solicitud utilizando el formulario superior.
                        </p>
                        <Button
                          variant="outline-primary"
                          onClick={() => {
                            resetearFormulario();
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          style={{ borderRadius: "10px" }}
                        >
                          <FaPlus className="me-2" />
                          Crear primera solicitud
                        </Button>
                      </div>
                    ) : (
                      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        <Table hover responsive className="mb-0" style={{ fontSize: "0.9rem" }}>
                          <thead 
                            style={{
                              backgroundColor: "#f8f9fa",
                              position: "sticky",
                              top: 0,
                              zIndex: 10,
                            }}
                          >
                            <tr>
                              <th className="border-0 py-3 px-4 text-secondary fw-semibold">Título</th>
                              <th className="border-0 py-3 px-4 text-secondary fw-semibold text-center" style={{ width: '15%' }}>Estado</th>
                              <th className="border-0 py-3 px-4 text-secondary fw-semibold text-center" style={{ width: '25%' }}>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {declaraciones.map(declaracion => (
                              <tr key={declaracion.id}>
                                <td className="py-3 px-4">
                                  <div className="d-flex align-items-center">
                                    <div
                                      className="rounded-2 p-2 me-3"
                                      style={{
                                        background: declaracion.activo 
                                          ? "linear-gradient(135deg, #198754 0%, #157347 100%)" 
                                          : "linear-gradient(135deg, #6c757d 0%, #5a6268 100%)",
                                        color: "white",
                                        minWidth: "40px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                      }}
                                    >
                                      <FaClipboardList size={14} />
                                    </div>
                                    <div>
                                      <div className="fw-medium text-dark">{declaracion.titulo}</div>
                                      <small className="text-muted">ID: {declaracion.id}</small>
                                    </div>
                                  </div>
                                </td>
                                <td className="text-center py-3 px-4">
                                  <Badge 
                                    bg={declaracion.activo ? "success" : "secondary"}
                                    className="px-3 py-2"
                                    style={{ borderRadius: "8px" }}
                                  >
                                    {declaracion.activo ? (
                                      <>
                                        <FaCheckCircle className="me-1" />
                                        Activo
                                      </>
                                    ) : (
                                      "Inactivo"
                                    )}
                                  </Badge>
                                </td>
                                <td className="text-center py-3 px-4">
                                  <div className="d-flex justify-content-center gap-1">
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={<Tooltip>Ver solicitud</Tooltip>}
                                    >
                                      <Button
                                        variant="outline-info"
                                        size="sm"
                                        onClick={() => verDeclaracion(declaracion)}
                                        style={{ borderRadius: "6px" }}
                                      >
                                        <FaEye />
                                      </Button>
                                    </OverlayTrigger>

                                    <OverlayTrigger
                                      placement="top"
                                      overlay={<Tooltip>Editar solicitud</Tooltip>}
                                    >
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => editarDeclaracion(declaracion.id)}
                                        style={{ borderRadius: "6px" }}
                                      >
                                        <FaEdit />
                                      </Button>
                                    </OverlayTrigger>

                                    <OverlayTrigger
                                      placement="top"
                                      overlay={<Tooltip>{declaracion.activo ? "Desactivar" : "Activar"}</Tooltip>}
                                    >
                                      <Button
                                        variant={declaracion.activo ? "outline-warning" : "outline-success"}
                                        size="sm"
                                        onClick={() => toggleActivoDeclaracion(declaracion.id, declaracion.activo)}
                                        style={{ borderRadius: "6px" }}
                                      >
                                        {declaracion.activo ? <FaToggleOff /> : <FaToggleOn />}
                                      </Button>
                                    </OverlayTrigger>

                                    <OverlayTrigger
                                      placement="top"
                                      overlay={<Tooltip>Eliminar solicitud</Tooltip>}
                                    >
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => eliminarDeclaracion(declaracion.id)}
                                        style={{ borderRadius: "6px" }}
                                      >
                                        <FaTrash />
                                      </Button>
                                    </OverlayTrigger>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>

                {/* Información adicional */}
                <Alert variant="info" className="border-0 shadow-sm mt-4" style={{ borderRadius: "10px" }}>
                  <div className="d-flex align-items-start">
                    <FaInfoCircle className="me-3 mt-1 fs-5" />
                    <div>
                      <h6 className="fw-bold mb-2">¿Cómo funcionan las solicitudes?</h6>
                      <div className="small">
                        <p className="mb-1">
                          • Las solicitudes son documentos personalizables que los empleados pueden completar y enviar
                        </p>
                        <p className="mb-1">
                          • Puede usar el editor rico para crear formatos profesionales y estructurados
                        </p>
                        <p className="mb-0">
                          • Una vez creadas, estarán disponibles en el proceso de onboarding para ser utilizadas por los empleados
                        </p>
                      </div>
                    </div>
                  </div>
                </Alert>
              </>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Modal para vista previa */}
      <Modal
        show={showVistaPrevia}
        onHide={() => setShowVistaPrevia(false)}
        size="lg"
        centered
        style={{ zIndex: 1055 }}
      >
        <Modal.Header 
          closeButton 
          className="border-0 pb-2"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white"
          }}
        >
          <Modal.Title className="d-flex align-items-center">
            <div
              className="rounded-3 p-2 me-3"
              style={{
                background: "rgba(255, 255, 255, 0.2)",
                color: "white",
              }}
            >
              <FaEye />
            </div>
            <div>
              <div>{declaracionPreview?.titulo}</div>
              <small className="opacity-75">Vista previa de la solicitud</small>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div
            className="border-0 p-4 bg-white rounded-3 shadow-sm"
            style={{ 
              minHeight: "300px",
              fontSize: "0.95rem",
              lineHeight: "1.6"
            }}
            dangerouslySetInnerHTML={{ __html: declaracionPreview?.texto }}
          />
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button 
            variant="primary"
            onClick={() => {
              setShowVistaPrevia(false);
              if (declaracionPreview) {
                editarDeclaracion(declaracionPreview.id);
              }
            }}
            style={{
              borderRadius: "8px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
            }}
          >
            <FaEdit className="me-2" />
            Editar
          </Button>
          <Button 
            variant="outline-secondary"
            onClick={() => setShowVistaPrevia(false)}
            style={{ borderRadius: "8px" }}
          >
            <FaTimes className="me-2" />
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Solicitud;


